import React, { useState, useEffect } from 'react';
import { Icon } from 'semantic-ui-react';
import { fetchUserSignedUrlPathV2 } from '../../utils/fetchSignedUrlByFolderV2';
import { config } from '../../constants/config';

/**
 * SignedImage — renders an <img> from an S3 path by first obtaining a signed URL.
 * Converts raw S3 keys (e.g. "aai-users/{userId}/litgroup-prototypes/…/bookCover/file.jpg")
 * to full HTTPS URLs before calling fetchUserSignedUrlPathV2, which requires the
 * https://aai-api.s3.us-west-1.amazonaws.com/ prefix.
 *
 * Props:
 *   s3Path       – the raw S3 key/path returned from the server
 *   userId       – current user ID for signing
 *   alt          – alt text
 *   style        – inline style for the <img>
 *   size         – shorthand: 'tiny' (40px), 'small' (60px), 'medium' (100px), 'large' (160px)
 *   fallbackIcon – Semantic icon name to show if no image / error (default: 'image outline')
 *   rounded      – if true, applies border-radius: 50%
 */
const SIZE_MAP = { tiny: 40, small: 60, medium: 100, large: 160 };

// S3 bucket base URL (with trailing slash from config)
const S3_BASE = config.AWS_AI_BUCKET || 'https://aai-api.s3.us-west-1.amazonaws.com/';

/**
 * Ensure the path is a full HTTPS S3 URL.
 * If it's already a full URL, return as-is. Otherwise prepend the bucket base.
 */
function toFullS3Url(s3Path) {
  if (!s3Path) return null;
  if (s3Path.startsWith('https://') || s3Path.startsWith('http://')) {
    return s3Path;
  }
  // Raw key like "aai-users/…/bookCover/file.jpg" → prepend bucket base
  return `${S3_BASE}${s3Path}`;
}

const SignedImage = ({
  s3Path,
  userId,
  alt = '',
  style = {},
  size = 'small',
  fallbackIcon = 'image outline',
  rounded = false,
  width = 'px',
  height = 'px',
}) => {
  const [signedUrl, setSignedUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const px = SIZE_MAP[size] || SIZE_MAP.small;

  useEffect(() => {
    let cancelled = false;

    if (!s3Path || !userId) {
      console.log('[SignedImage] No s3Path or userId — s3Path:', s3Path, 'userId:', userId);
      setSignedUrl(null);
      return;
    }

    const fetchUrl = async () => {
      setLoading(true);
      setError(false);
      try {
        const fullUrl = toFullS3Url(s3Path);
        console.log('[SignedImage] Fetching signed URL for:', fullUrl);
        const url = await fetchUserSignedUrlPathV2(userId, fullUrl);
        console.log('[SignedImage] Got signed URL:', url ? url.substring(0, 80) + '...' : 'null');
        if (!cancelled) setSignedUrl(url || null);
      } catch (err) {
        console.warn('[SignedImage] Failed to sign:', s3Path, err);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchUrl();
    return () => { cancelled = true; };
  }, [s3Path, userId]);

  const containerStyle = {
    width: px,
    height: px,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    backgroundColor: '#f0f0f0',
    borderRadius: rounded ? '50%' : 4,
    flexShrink: 0,
    ...style,
  };

  if (!s3Path) {
    return (
      <div style={containerStyle}>
        <Icon name={fallbackIcon} size="large" color="grey" style={{ margin: 0 }} />
      </div>
    );
  }

  if (loading) {
    return (
      <div style={containerStyle}>
        <Icon name="spinner" loading size="large" color="grey" style={{ margin: 0 }} />
      </div>
    );
  }

  if (error || !signedUrl) {
    return (
      <div style={containerStyle}>
        <Icon name={fallbackIcon} size="large" color="grey" style={{ margin: 0 }} />
      </div>
    );
  }

  return (
    <img
      src={signedUrl}
      alt={alt}
      style={{
        width: width,
        height: height,
        objectFit: 'cover',
        borderRadius: rounded ? '50%' : 4,
        // Never let a flex parent (e.g. the author cell's row: image + nowrap name) compress the
        // image's width while its height stays fixed — that squish is what made it oblong.
        flexShrink: 0,
        ...style,
      }}
      onError={() => {
        console.warn('[SignedImage] Image load error for:', s3Path);
        setError(true);
      }}
    />
  );
};

export default SignedImage;
